const DASHBOARD_ROLES = new Set([
  'ADMIN',
  'SUPER_ADMIN',
  'CUSTOMER',
  'COLLECTION_EXECUTIVE',
  'FRONT_DESK',
  'SETTLEMENT_MANAGER',
  'FINANCE_MANAGER',
  'DEMO_EXECUTIVE',
]);

const ROLE_LABELS = {
  ADMIN: 'Company Admin',
  SUPER_ADMIN: 'Super Admin',
  CUSTOMER: 'Customer',
  COLLECTION_EXECUTIVE: 'Collection Executive',
  FRONT_DESK: 'Front Desk',
  SETTLEMENT_MANAGER: 'Settlement Manager',
  FINANCE_MANAGER: 'Finance Manager',
  DEMO_EXECUTIVE: 'Demo Executive',
  VISITOR: 'Visitor',
  GUEST: 'Guest',
};

export function getRedirectBase() {
  return process.env.REACT_APP_REDIRECT_URL || 'https://testapp.chitbid.com';
}

/** Fired whenever landing auth tokens/role/mobile change so open pages can refresh UI. */
export const LANDING_AUTH_EVENT = 'chitbid-landing-auth';

function notifyAuthChanged() {
  try {
    window.dispatchEvent(new Event(LANDING_AUTH_EVENT));
  } catch {
    /* ignore */
  }
}

export function storeAuthSession(result, mobile) {
  if (!result?.tokens) return false;
  const roleName =
    result.user?.role?.name ||
    result.user?.roleType ||
    result.user?.roles?.[0]?.name;
  if (!roleName) return false;
  localStorage.setItem('access-token', result.tokens.accessToken);
  localStorage.setItem('role', btoa(roleName));
  localStorage.setItem('refreshToken', result.tokens.refreshToken);
  localStorage.setItem('userId', result.user._id);
  if (mobile) localStorage.setItem('mobile', mobile);
  // Guests often have no name — never keep a previous account's name around.
  const name = String(result.user?.name || '').trim();
  if (name) localStorage.setItem('name', name);
  else localStorage.removeItem('name');
  notifyAuthChanged();
  return true;
}

export function clearLandingSession() {
  localStorage.removeItem('access-token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('role');
  localStorage.removeItem('userId');
  localStorage.removeItem('mobile');
  localStorage.removeItem('name');
  try {
    sessionStorage.clear();
  } catch {
    /* ignore */
  }
  notifyAuthChanged();
}

export function clearAuthSession() {
  clearLandingSession();
}

export function getSessionRole() {
  try {
    return atob(localStorage.getItem('role') || '') || null;
  } catch {
    return null;
  }
}

export function isGuestLoggedIn() {
  const token = localStorage.getItem('access-token');
  if (!token) return false;
  return getSessionRole() === 'GUEST';
}

/** Live snapshot for Join UI — never trust stale React state alone. */
export function getGuestSessionSnapshot() {
  const loggedIn = isGuestLoggedIn();
  return {
    loggedIn,
    mobile: loggedIn ? localStorage.getItem('mobile') || '' : '',
  };
}

const HANDOFF_READY = 'CHITBID_HANDOFF_READY';
const HANDOFF_MSG = 'CHITBID_AUTH_HANDOFF';
const HANDOFF_CANCEL = 'CHITBID_HANDOFF_CANCEL';
const HANDOFF_DONE = 'CHITBID_HANDOFF_DONE';

let pendingHandoffPayload = null;
let handoffListenerBound = false;
let handoffFallbackTimer = null;

function getRedirectOrigin() {
  try {
    return new URL(getRedirectBase()).origin;
  } catch {
    return '*';
  }
}

function getHandoffPayload(target) {
  return {
    accessToken: localStorage.getItem('access-token') || '',
    refreshToken: localStorage.getItem('refreshToken') || '',
    roleName: localStorage.getItem('role') || '',
    userId: localStorage.getItem('userId') || '',
    mobile: localStorage.getItem('mobile') || '',
    target: target || '/dashboard',
  };
}

function buildReconnectHashUrl(payload) {
  const redirectUrl = getRedirectBase().replace(/\/$/, '');
  const params = new URLSearchParams();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value) params.set(key, String(value));
  });
  return `${redirectUrl}/reconnect/#${params.toString()}`;
}

function clearHandoffFallback() {
  if (handoffFallbackTimer != null) {
    window.clearTimeout(handoffFallbackTimer);
    handoffFallbackTimer = null;
  }
}

function ensureHandoffListener() {
  if (handoffListenerBound) return;
  handoffListenerBound = true;
  window.addEventListener('message', (event) => {
    if (event.data?.type === HANDOFF_DONE) {
      clearHandoffFallback();
      pendingHandoffPayload = null;
      return;
    }
    if (event.data?.type !== HANDOFF_READY) return;
    if (!pendingHandoffPayload || !event.source) return;
    try {
      event.source.postMessage(
        { type: HANDOFF_MSG, payload: pendingHandoffPayload },
        event.origin || getRedirectOrigin(),
      );
    } catch {
      /* ignore */
    }
  });
}

/**
 * Open app via /reconnect.
 * Prefer a pre-opened reconnect tab + postMessage so JWTs are not put in the
 * URL (long query strings get truncated → broken token + wrong menus).
 */
export function handoffToApp({
  target,
  sameTab = false,
  preopenedWindow = null,
} = {}) {
  const payload = getHandoffPayload(target);

  const goSameTab = () => {
    const url = buildReconnectHashUrl(payload);
    clearLandingSession();
    window.location.assign(url);
    return { mode: 'same-tab' };
  };

  if (sameTab || !payload.accessToken) {
    return goSameTab();
  }

  ensureHandoffListener();

  if (preopenedWindow && !preopenedWindow.closed) {
    pendingHandoffPayload = payload;
    try {
      preopenedWindow.postMessage(
        { type: HANDOFF_MSG, payload },
        getRedirectOrigin(),
      );
    } catch {
      /* READY listener will retry */
    }

    clearHandoffFallback();
    // Only if reconnect never ACK'd — avoid yanking a successful dashboard tab.
    handoffFallbackTimer = window.setTimeout(() => {
      handoffFallbackTimer = null;
      if (!pendingHandoffPayload) return;
      if (!preopenedWindow || preopenedWindow.closed) return;
      try {
        preopenedWindow.location.replace(buildReconnectHashUrl(payload));
      } catch {
        /* ignore */
      }
    }, 4000);

    clearLandingSession();
    return { mode: 'new-tab' };
  }

  const opened = window.open(buildReconnectHashUrl(payload), '_blank');
  if (opened && !opened.closed) {
    clearLandingSession();
    return { mode: 'new-tab' };
  }

  return goSameTab();
}

/** Open app reconnect tab during a user gesture; finish via postMessage after pay. */
export function openHandoffPlaceholder() {
  ensureHandoffListener();
  const redirectUrl = getRedirectBase().replace(/\/$/, '');
  try {
    return window.open(
      `${redirectUrl}/reconnect/?awaitHandoff=1`,
      'chitbid_app_handoff',
    );
  } catch {
    return null;
  }
}

export function closeHandoffPlaceholder(win) {
  clearHandoffFallback();
  pendingHandoffPayload = null;
  if (!win || win.closed) return;
  try {
    win.postMessage({ type: HANDOFF_CANCEL }, getRedirectOrigin());
  } catch {
    /* ignore */
  }
  try {
    win.close();
  } catch {
    /* ignore */
  }
}

/**
 * After join payment, re-issue tokens so role becomes CUSTOMER (DB already promoted).
 * Retries briefly — webhook promotion and refresh can race by a few hundred ms.
 */
export async function refreshLandingSession(
  refreshRequest,
  { expectRole = null, attempts = 1, delayMs = 600 } = {},
) {
  if (typeof refreshRequest !== 'function') {
    return { ok: false, message: 'Refresh unavailable' };
  }

  let lastMessage = 'Could not refresh session';
  for (let i = 0; i < attempts; i += 1) {
    try {
      const res = await refreshRequest();
      if (!res || res.status >= 400) {
        lastMessage = res?.data?.msg || lastMessage;
      } else {
        const result = res.data?.result;
        const mobile = localStorage.getItem('mobile') || '';
        if (!storeAuthSession(result, mobile)) {
          lastMessage = 'Could not update session';
        } else {
          const role = getSessionRole();
          if (!expectRole || role === expectRole) {
            return { ok: true, role, result };
          }
          lastMessage = `Still signed in as ${role || 'unknown'}; waiting for Customer…`;
        }
      }
    } catch (err) {
      lastMessage = err?.message || lastMessage;
    }
    if (i < attempts - 1) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }

  return { ok: false, message: lastMessage, role: getSessionRole() };
}

export function openAppUrl(url) {
  window.open(url, '_blank');
}

export function buildReconnectUrl({ target } = {}) {
  const redirectUrl = getRedirectBase();
  const params = new URLSearchParams({
    accessToken: localStorage.getItem('access-token') || '',
    refreshToken: localStorage.getItem('refreshToken') || '',
    roleName: localStorage.getItem('role') || '',
  });
  const userId = localStorage.getItem('userId');
  if (userId) params.set('userId', userId);
  const mobile = localStorage.getItem('mobile');
  if (mobile) params.set('mobile', mobile);
  if (target) params.set('target', target);
  return `${redirectUrl}/reconnect/?${params.toString()}`;
}

export function buildForceChangePasswordUrl(result) {
  const redirectUrl = getRedirectBase();
  const params = new URLSearchParams({
    email: result.email || '',
    userId: result.userId || '',
  });
  return `${redirectUrl}/force-change-password?${params.toString()}`;
}

export function resolveLandingAction(result) {
  if (result?.landingRedirect) {
    return result.landingRedirect;
  }

  const role = result?.user?.role?.name;
  const status = result?.currentStatus;

  if (role === 'VISITOR') {
    if (status === 'registration_pending') {
      return {
        action: 'show_registration_form',
        role,
        message: 'Complete your company registration.',
      };
    }
    return {
      action: 'continue_onboarding',
      role,
      message: 'Continue your company onboarding.',
    };
  }

  if (role === 'GUEST') {
    return {
      action: 'stay_on_guest',
      role,
      message: "You're verified. You can explore chits now.",
    };
  }

  if (DASHBOARD_ROLES.has(role)) {
    const label = ROLE_LABELS[role] || role;
    return {
      action: 'go_to_dashboard',
      role,
      message: `This mobile is linked to your ${label} account. Opening your dashboard.`,
    };
  }

  return {
    action: 'go_to_login',
    role,
    message: `This mobile is linked to a ${ROLE_LABELS[role] || role} account. Company registration is not available here.`,
  };
}

export function getReconnectTarget(action, role) {
  if (action === 'continue_onboarding') return '/visitor/visitorPage';
  if (action === 'go_to_dashboard' || DASHBOARD_ROLES.has(role)) return '/dashboard';
  if (action === 'go_to_login') return '/login';
  return '/visitor/visitorPage';
}

/** Hints after OTP send on company (visitor) registration. */
export function showExistingAccountHint(sendResult) {
  const meta = sendResult?.data?.result;
  if (!meta?.existingAccount) return null;

  if (meta.canRegisterCompany) {
    return 'Welcome back. Complete your company registration after OTP verification.';
  }

  const label = ROLE_LABELS[meta.role] || meta.role || 'registered';
  if (meta.role === 'VISITOR') {
    return 'Welcome back. You will continue your company onboarding after OTP verification.';
  }
  if (meta.role === 'GUEST') {
    return 'This number is used to join chits. Open Join a chit to continue, or use a different number to set up a business.';
  }
  if (DASHBOARD_ROLES.has(meta.role)) {
    return `This mobile is linked to a ${label} account. After OTP you will be taken to your dashboard.`;
  }
  return `This mobile is linked to a ${label} account. Company registration is not available on this page.`;
}

/** Hints after OTP send on guest join flow. */
export function showGuestAccountHint(sendResult) {
  const meta = sendResult?.data?.result;
  if (!meta?.existingAccount) return null;

  if (meta.role === 'GUEST') {
    return 'Welcome back. After OTP you can continue with this chit.';
  }
  if (meta.role === 'VISITOR') {
    return 'This number is already used to set up a business. Try another number, or go Home to continue that setup.';
  }
  if (DASHBOARD_ROLES.has(meta.role)) {
    const label = ROLE_LABELS[meta.role] || meta.role;
    return `This mobile is linked to a ${label} account. After OTP you will open the app.`;
  }
  return `This mobile is linked to a ${ROLE_LABELS[meta.role] || meta.role} account.`;
}

export function handleVerifyResponse(updateApi, mobile, callbacks = {}) {
  const { onShowRegistrationForm, onCloseModals, onGuestReady } = callbacks;

  if (!updateApi || updateApi.status >= 400) {
    return {
      ok: false,
      message: updateApi?.data?.msg || 'OTP verification failed',
    };
  }

  const result = updateApi.data?.result;

  const closeUi = ({ clearInputs = true, action } = {}) => {
    onCloseModals?.({ clearInputs, action });
  };

  if (result?.mustChangePassword) {
    openAppUrl(buildForceChangePasswordUrl(result));
    clearLandingSession();
    closeUi({ clearInputs: true, action: 'force_change_password' });
    return {
      ok: true,
      action: 'force_change_password',
      message: result.landingRedirect?.message || 'Please set a new password to continue.',
    };
  }

  const landing = resolveLandingAction(result);

  if (landing.action === 'show_registration_form') {
    if (!storeAuthSession(result, mobile)) {
      return { ok: false, message: 'Could not sign you in. Please try again.' };
    }
    closeUi({ clearInputs: false, action: landing.action });
    onShowRegistrationForm?.();
    return { ok: true, action: landing.action, message: landing.message };
  }

  if (landing.action === 'stay_on_guest') {
    if (!storeAuthSession(result, mobile)) {
      return { ok: false, message: 'Could not sign you in. Please try again.' };
    }
    // Resume pending action (interest / pay) BEFORE closing UI — parent onClose
    // often clears the pending sessionStorage entry.
    onGuestReady?.();
    closeUi({ clearInputs: true, action: landing.action });
    return { ok: true, action: landing.action, message: landing.message };
  }

  if (landing.action === 'go_to_login') {
    clearLandingSession();
    openAppUrl(`${getRedirectBase()}/login`);
    closeUi({ clearInputs: true, action: landing.action });
    return { ok: true, action: landing.action, message: landing.message };
  }

  if (!storeAuthSession(result, mobile)) {
    return { ok: false, message: 'Could not sign you in. Please try again.' };
  }

  const target = getReconnectTarget(landing.action, landing.role);
  handoffToApp({ target });
  closeUi({ clearInputs: true, action: landing.action });
  return { ok: true, action: landing.action, message: landing.message };
}
